import * as Yup from "yup";
import nfSaidaPagamentosValidator from "./nf-saida-pagamentos.validator";
import nfSaidaItensValidator from "./nf-saida-itens.validator";
import nfSaidaReferenciaValidator from "./nf-saida-referencias.validator";

const nfSaidaValidator = ({ operacaoFiscal, documentoTransferencia }) => {
  return Yup.object().shape({
    serieDocumento: Yup.mixed().when(
      [
        "importacaoDocumento",
        "documentoDevolucaoCliente",
        "documentoDevolucaoFornecedor",
      ],
      {
        is: (devolucaoCliente, devolucaoFornecedor) =>
          devolucaoCliente || devolucaoFornecedor,
        then: Yup.mixed().nullable().required("Série Documento"),
        otherwise: Yup.mixed().nullable(),
      },
    ),
    orcamentoImport: Yup.boolean().nullable(),
    orcamentoIds: Yup.array().when("orcamentoImport", {
      is: true,
      then: Yup.array().of(Yup.number()).min(1, "Orçamento ID Importação"),
      otherwise: Yup.array().nullable(),
    }),
    condicionalImport: Yup.boolean().nullable(),
    condicionalIds: Yup.array().when("condicionalImport", {
      is: true,
      then: Yup.array().of(Yup.number()).min(1, "Condicional ID Importação"),
      otherwise: Yup.array().nullable(),
    }),
    pedidoImport: Yup.boolean().nullable(),
    pedidoIds: Yup.array().when("pedidoImport", {
      is: true,
      then: Yup.array().of(Yup.number()).min(1, "Pedido ID Importação"),
      otherwise: Yup.array().nullable(),
    }),
    ordemServicoImport: Yup.boolean().nullable(),
    ordemServicoIds: Yup.array().when("ordemServicoImport", {
      is: true,
      then: Yup.array().of(Yup.number()).min(1, "Ordem Serviço ID Importação"),
      otherwise: Yup.array().nullable(),
    }),
    dataEmissao: Yup.date().nullable().required("Data Emissão"),
    dataPrevisaoEntrega: Yup.date()
      .nullable()
      .min(new Date(), "Data posterior ao dia atual"),
    operacaoFiscalId: Yup.number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .nullable()
      .required("Operação Fiscal"),
    operacaoFiscal: Yup.object()
      .shape({
        movimentacaoFinanceira: Yup.boolean().nullable().required(),
      })
      .nullable()
      .required(),
    clienteId:
      operacaoFiscal?.modeloDocumento === "55" &&
      operacaoFiscal?.finalidade !== "DEVOLUCAO" &&
      !documentoTransferencia
        ? Yup.number().nullable().required("Cliente")
        : Yup.number().nullable(),
    clienteEmail: Yup.string().nullable(),
    clienteNome:
      operacaoFiscal?.modeloDocumento === "55" &&
      operacaoFiscal?.finalidade !== "DEVOLUCAO" &&
      !documentoTransferencia
        ? Yup.string().nullable().required("Cliente Nome")
        : Yup.string().nullable(),
    clienteCnpjCpf: Yup.string().when(["nfSaidaEnderecoCliente"], {
      is: (endereco) => {
        return (
          operacaoFiscal?.modeloDocumento === "55" &&
          operacaoFiscal?.finalidade !== "DEVOLUCAO" &&
          !documentoTransferencia &&
          (!endereco || (!endereco?.pais && !endereco?.bacen))
        );
      },
      then: Yup.string().nullable().required("Cliente Cnpj / Cpf"),
      otherwise: Yup.string().nullable(),
    }),
    clienteInscricaoSuframa: Yup.string().nullable(),
    clienteInscricaoEstadual: Yup.string().nullable(),
    prestadorServicoId: Yup.number().nullable(),
    vendedorId: Yup.number().when("vendedorObrigatorio", {
      is: true,
      then: Yup.number().nullable().required("Vendedor"),
      otherwise: Yup.number().nullable(),
    }),
    vendedorObrigatorio: Yup.boolean().nullable(),
    modalidadeFrete: Yup.string()
      .nullable()
      .when("dataPrevisaoEntrega", {
        is: (data) => !!data,
        then: (schema) =>
          schema.notOneOf(["mfSemFrete"], "Ocorrência de Transporte/Frete"),
      }),
    transportadoraId: Yup.number().nullable(),
    transportadoraNome: Yup.string().when(
      ["transportadoraId", "documentoDevolucaoFornecedor"],
      {
        is: (transportadoraId, documentoDevolucaoFornecedor) =>
          transportadoraId && documentoDevolucaoFornecedor,
        then: Yup.string().nullable().required("Nome Transportadora"),
        otherwise: Yup.string().nullable(),
      },
    ),
    transportadoraCnpjCpf: Yup.string().when(
      ["transportadoraId", "documentoDevolucaoFornecedor"],
      {
        is: (transportadoraId, documentoDevolucaoFornecedor) =>
          transportadoraId && documentoDevolucaoFornecedor,
        then: Yup.string().nullable().required("Cnpj/Cpf Transportadora"),
        otherwise: Yup.string().nullable(),
      },
    ),
    transportadoraIe: Yup.string().nullable(),
    transportadoraEnderecoRua: Yup.string().when(
      ["transportadoraId", "documentoDevolucaoFornecedor", "operacaoEntrada"],
      {
        is: (transportadoraId, documentoDevolucaoFornecedor, operacaoEntrada) =>
          transportadoraId && (documentoDevolucaoFornecedor || operacaoEntrada),
        then: Yup.string().nullable().required("O endereço da transportadora"),
        otherwise: Yup.string().nullable(),
      },
    ),
    transportadoraEnderecoMunicipio: Yup.string().when(
      ["transportadoraId", "documentoDevolucaoFornecedor", "operacaoEntrada"],
      {
        is: (transportadoraId, documentoDevolucaoFornecedor, operacaoEntrada) =>
          transportadoraId && (documentoDevolucaoFornecedor || operacaoEntrada),
        then: Yup.string().nullable().required("O endereço da transportadora"),
        otherwise: Yup.string().nullable(),
      },
    ),
    transportadoraEnderecoUf: Yup.string().when(
      ["transportadoraId", "documentoDevolucaoFornecedor", "operacaoEntrada"],
      {
        is: (transportadoraId, documentoDevolucaoFornecedor, operacaoEntrada) =>
          transportadoraId && (documentoDevolucaoFornecedor || operacaoEntrada),
        then: Yup.string().nullable().required("O endereço da transportadora"),
        otherwise: Yup.string().nullable(),
      },
    ),
    qtdVolumes: Yup.number().nullable(),
    pesoBruto: Yup.number().nullable(),
    pesoLiquido: Yup.number().nullable(),
    produtoMarca: Yup.string().nullable(),
    produtoNumero: Yup.string().nullable(),
    especieVolume: Yup.string().nullable(),
    nfSaidaEnderecoCliente: Yup.object().nullable(),
    freteCompoeNota: Yup.boolean().nullable(),
    informacoesAdicionais: Yup.string().nullable(),
    informacoesFisco: Yup.string().nullable(),
    desconto: Yup.number().nullable(),
    acrescimo: Yup.number().nullable(),
    despesas: Yup.number().nullable(),
    frete: Yup.number().nullable(),
    quantidadeParcelas: Yup.number().nullable(),
    importacaoDocumento: Yup.boolean().nullable(),
    documentoDevolucaoCliente: Yup.boolean().nullable(),
    documentoDevolucaoFornecedor: Yup.boolean().nullable(),
    presencaConsumidor: Yup.string().required("Presença do consumidor"),
    nfSaidaImportadaId: Yup.number().when("importacaoDocumento", {
      is: true,
      then: Yup.number().nullable().required("NF Saida ID Importação "),
      otherwise: Yup.number().nullable(),
    }),
    nfSaidaItens: nfSaidaItensValidator(operacaoFiscal),
    nfSaidaPagamentos: operacaoFiscal?.movimentacaoFinanceira
      ? nfSaidaPagamentosValidator()
      : null,
    nfSaidaReferencias: nfSaidaReferenciaValidator(),
    totalVenda: Yup.number().nullable().required("Total venda"),
  });
};

export default nfSaidaValidator;
